import React from 'react';
import { useTranslation } from 'react-i18next';
import { Results } from '../../types/results';

interface Props {
  results: Results;
}

const SummaryTab: React.FC<Props> = ({ results }) => {
  const { t } = useTranslation();
  
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(results.language === 'arabic' ? 'ar' : results.language === 'french' ? 'fr-FR' : 'en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatScore = (score: number | undefined) => {
    if (score === undefined || score === null) return '-';
    return score.toFixed(1);
  };

  const correctAnswers = results.answers.filter(a => a.is_correct).length;
  const avgTimePerQuestion = Math.round(results.total_duration_seconds / results.answers.length);
  const adjustments = results.scores.score_adjustments;

  // Get adjustment labels based on language
  const getAdjustmentRows = () => {
    const rows: { label: string; value: number; isPositive: boolean }[] = [];
    
    if (results.language === 'french') {
      if (adjustments.bonus_etudes) rows.push({ label: t('results.bonusEducation') || 'Bonus études', value: adjustments.bonus_etudes, isPositive: true });
      if (adjustments.bonus_domaine) rows.push({ label: t('results.bonusDomain') || 'Bonus domaine', value: adjustments.bonus_domaine, isPositive: true });
      if (adjustments.bonus_metier) rows.push({ label: t('results.bonusProfession') || 'Bonus métier', value: adjustments.bonus_metier, isPositive: true });
      if (adjustments.malus_age) rows.push({ label: t('results.malusAge') || 'Malus âge', value: adjustments.malus_age, isPositive: false });
    } else if (results.language === 'arabic') {
      if (adjustments.bonus_arabophone) rows.push({ label: t('results.bonusArabophone') || 'Bonus arabophone', value: adjustments.bonus_arabophone, isPositive: true });
      if (adjustments.bonus_etudes_ar) rows.push({ label: t('results.bonusEducation') || 'Bonus études', value: adjustments.bonus_etudes_ar, isPositive: true });
      if (adjustments.malus_age) rows.push({ label: t('results.malusAge') || 'Malus âge', value: adjustments.malus_age, isPositive: false });
    } else if (results.language === 'english') {
      if (adjustments.bonus_exposition) rows.push({ label: t('results.bonusExposure') || 'Bonus exposition', value: adjustments.bonus_exposition, isPositive: true });
      if (adjustments.malus_niveau) rows.push({ label: t('results.malusLevel') || 'Malus niveau', value: adjustments.malus_niveau, isPositive: false });
    }
    
    return rows;
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-bold text-gray-800">{t('results.overview')}</h3>
        <p className="text-sm text-gray-500">{t('results.summaryDescription')}</p>
      </div>

      {/* ETALONNAGE Scores - Main highlight */}
      <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl p-4 border border-primary/20">
        <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
          {t('results.etalonnageScores')}
        </h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <ScoreCard 
            label={t('results.rawScore')} 
            value={`${results.scores.raw_score}/${results.scores.max_score}`}
            sublabel={t('results.correctAnswers')}
          />
          <ScoreCard 
            label={t('results.adjustedScore')} 
            value={`${formatScore(results.scores.adjusted_score)}/${results.scores.max_score}`}
            sublabel={`${adjustments.total_adjustment >= 0 ? '+' : ''}${adjustments.total_adjustment} pts`}
            highlight
          />
          <ScoreCard 
            label={t('results.speedScore')} 
            value={formatScore(results.scores.speed_score)}
            sublabel={t('results.timeEfficiency')}
          />
          <ScoreCard 
            label={t('results.finalScore')} 
            value={`${formatScore(results.scores.final_score)}/40`}
            sublabel={`${results.scores.final_score_percentage || Math.round((results.scores.final_score / 40) * 100)}%`}
            highlight
          />
        </div>
        <p className="text-xs text-gray-500 mt-3 text-center">
          {t('results.percentile')}: {results.scores.percentile}%
        </p>
      </div>

      {/* Score Adjustments Breakdown */}
      {adjustments.total_adjustment !== 0 && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold text-gray-800 mb-2 text-sm">{t('results.adjustmentsDetail')}</h4>
          <div className="space-y-1.5">
            {getAdjustmentRows().map((row, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span className="text-gray-600">{row.label}</span>
                <span className={row.isPositive ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                  {row.isPositive ? '+' : ''}{row.value}
                </span>
              </div>
            ))}
            <div className="border-t pt-1.5 mt-1.5 flex justify-between text-sm font-semibold">
              <span className="text-gray-700">{t('results.totalAdjustment')}</span>
              <span className={adjustments.total_adjustment >= 0 ? 'text-green-600' : 'text-red-600'}>
                {adjustments.total_adjustment >= 0 ? '+' : ''}{adjustments.total_adjustment}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Info Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Session Info */}
        <InfoCard title={t('results.session')}>
          <InfoRow label={t('results.language')} value={results.language.toUpperCase()} />
          <InfoRow label={t('results.version')} value={results.test_version} />
          <InfoRow label={t('results.date')} value={formatDate(results.started_at)} />
          <InfoRow label={t('results.status')} value={results.status === 'completed' ? t('results.completed') : results.status} />
        </InfoCard>

        {/* Profile Summary */}
        <InfoCard title={t('results.profile')}>
          <InfoRow label={t('results.profile')} value={results.profile.profile_name} />
          <InfoRow label={t('results.level')} value={results.profile.level} />
          <InfoRow label={t('results.percentile')} value={results.profile.percentile_level || `${results.scores.percentile}%`} />
        </InfoCard>

        {/* Time Stats */}
        <InfoCard title={t('results.time')}>
          <InfoRow label={t('results.totalDuration')} value={formatDuration(results.total_duration_seconds)} />
          <InfoRow label={t('results.pace')} value={results.profile.tempo_label || results.profile.tempo} />
          <InfoRow label={t('results.avgTimePerQuestion')} value={`${avgTimePerQuestion}s/question`} />
        </InfoCard>

        {/* Answers Stats */}
        <InfoCard title={t('results.answers')}>
          <InfoRow label={t('results.totalQuestions')} value={results.answers.length.toString()} />
          <InfoRow label={t('results.correct')} value={correctAnswers.toString()} valueColor="text-green-600" />
          <InfoRow label={t('results.incorrect')} value={(results.answers.length - correctAnswers).toString()} valueColor="text-red-600" />
        </InfoCard>
      </div>

      {/* Dimension Scores */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-gray-800 mb-3">{t('results.performanceBySection')}</h4>
        <div className="space-y-3">
          {Object.entries(results.dimension_scores).map(([key, val]) => (
            <div key={key} className="space-y-1">
              <div className="flex justify-between items-center text-sm">
                <span className="capitalize text-gray-700 font-medium">{t(`results.section.${key}`) || key}</span>
                <span className="font-semibold text-gray-800">
                  {val.score}/{val.max} ({val.percentage}%)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${getProgressColor(val.percentage)}`}
                  style={{ width: `${val.percentage}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Score card for ETALONNAGE scores
interface ScoreCardProps {
  label: string;
  value: string;
  sublabel?: string;
  highlight?: boolean;
}

const ScoreCard: React.FC<ScoreCardProps> = ({ label, value, sublabel, highlight }) => (
  <div className={`text-center p-3 rounded-lg ${highlight ? 'bg-white shadow-sm' : 'bg-white/50'}`}>
    <div className="text-xs text-gray-500 mb-1">{label}</div>
    <div className={`text-xl font-bold ${highlight ? 'text-primary' : 'text-gray-800'}`}>{value}</div>
    {sublabel && <div className="text-xs text-gray-400 mt-0.5">{sublabel}</div>}
  </div>
);

// Info card wrapper
interface InfoCardProps {
  title: string;
  children: React.ReactNode;
}

const InfoCard: React.FC<InfoCardProps> = ({ title, children }) => (
  <div className="bg-gray-50 rounded-lg p-3">
    <h4 className="font-semibold text-gray-800 mb-2 text-sm">{title}</h4>
    <div className="space-y-1.5">
      {children}
    </div>
  </div>
);

// Info row
interface InfoRowProps {
  label: string;
  value: string;
  valueColor?: string;
}

const InfoRow: React.FC<InfoRowProps> = ({ label, value, valueColor = 'text-gray-800' }) => (
  <div className="flex justify-between text-sm">
    <span className="text-gray-500">{label}</span>
    <span className={`font-medium ${valueColor}`}>{value}</span>
  </div>
);

// Get color based on percentage
const getProgressColor = (percentage: number): string => {
  if (percentage >= 80) return 'bg-green-500';
  if (percentage >= 60) return 'bg-primary';
  if (percentage >= 40) return 'bg-yellow-500';
  return 'bg-red-500';
};

export default SummaryTab;
